import React, { memo, useState, useEffect } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import IntlMessages from 'util/IntlMessages';

import styles from './styles.module.less';

const applyMask = (value, mask) => {
  if (!value || !mask) return value;
  
  const cleanValue = value.replace(/[^\w]/g, '');
  let maskedValue = '';
  let valueIndex = 0;
  
  for (let i = 0; i < mask.length && valueIndex < cleanValue.length; i++) {
    if (mask[i] === '#') {
      maskedValue += cleanValue[valueIndex];
      valueIndex++;
    } else {
      maskedValue += mask[i];
    }
  }
  
  return maskedValue;
};

const CustomInput = ({
  className,
  label,
  textClassName,
  errors,
  register,
  onKeyDown,
  autoFocus,
  mask,
  currentValue,
  ...props
}) => {
  const name = register.name;
  const [displayValue, setDisplayValue] = useState('');
  const originalOnChange = register.onChange;

  // Inicializar displayValue cuando se usa mask
  useEffect(() => {
    if (mask) {
      const getFieldValue = () => {
        // Priorizar currentValue (viene de useWatch), luego otras fuentes
        return currentValue || 
               register.ref?.current?.value || 
               register.ref?.current?.defaultValue || 
               props.defaultValue || 
               props.value || 
               '';
      };
      
      const fieldValue = getFieldValue();
      if (fieldValue) {
        const maskedValue = applyMask(fieldValue, mask);
        setDisplayValue(maskedValue);
      } else {
        setDisplayValue('');
      }
    }
  }, [mask, currentValue, register.ref?.current?.value, register.ref?.current?.defaultValue, props.defaultValue, props.value]);

  const handleChange = (event) => {
    const rawValue = event.target.value;
    let formattedValue = rawValue;

    if (mask) {
      formattedValue = applyMask(rawValue, mask);
      setDisplayValue(formattedValue);
      
      const modifiedEvent = {
        ...event,
        target: {
          ...event.target,
          value: formattedValue,
        },
      };

      if (originalOnChange) {
        originalOnChange(modifiedEvent);
      }
    } else {
      if (originalOnChange) {
        originalOnChange(event);
      }
    }
  };

  const inputProps = {
    autoComplete: 'off',
    className: classnames(
      'm-bottom-1 ant-input',
      styles.input,
      !!errors[name]?.message && styles.error,
    ),
    ...register,
    ...props,
    autoFocus,
    onKeyDown,
  };

  // Solo sobrescribir value y onChange cuando se use máscara
  if (mask) {
    inputProps.value = displayValue;
    inputProps.onChange = handleChange;
  }

  return (
    <div className={classnames(styles.mainContainer, className)}>
      <label className={classnames('m-bottom-1', textClassName)} htmlFor={name}>
        {label}
      </label>
      <input {...inputProps} />
      {!!errors[name]?.message && <span className={styles.errorText}>{errors[name]?.message}</span>}
      {register.required && (
        <span className={classnames(styles.required, textClassName)}>
          <IntlMessages id="form.required" />
        </span>
      )}
    </div>
  );
};

CustomInput.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  register: PropTypes.shape({
    name: PropTypes.string.isRequired,
    onBlur: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    ref: PropTypes.any.isRequired,

    disabled: PropTypes.bool,
    max: PropTypes.number,
    maxLength: PropTypes.number,
    min: PropTypes.number,
    minLength: PropTypes.number,
    pattern: PropTypes.string,
    required: PropTypes.bool,
  }).isRequired,
  errors: PropTypes.objectOf(
    PropTypes.shape({
      message: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    }),
  ).isRequired,

  onKeyDown: PropTypes.func,
  autoFocus: PropTypes.bool,
  className: PropTypes.string,
  textClassName: PropTypes.string,
  mask: PropTypes.string,
  currentValue: PropTypes.string,
};

CustomInput.defaultProps = {
  className: '',
  textClassName: '',
  onKeyDown: () => {},
  autoFocus: false,
  mask: null,
  currentValue: '',
};

export default memo(CustomInput);
